import { z } from "zod";

// Base showcase item schemas
export const showcaseItemMetadataSchema = z.object({
  capabilities: z.array(z.string()).optional(),
  promptCount: z.number().optional(),
  complexity: z.enum(["beginner", "intermediate", "advanced"]).optional(),
  averageRating: z.number().min(0).max(5).optional(),
  reviewCount: z.number().optional(),
  useCases: z.array(z.string()).optional(),
  exampleInputs: z.array(z.string()).optional(),
  exampleOutputs: z.array(z.string()).optional(),
  integrations: z.array(z.string()).optional(),
}).catchall(z.any());

export const showcaseItemSchema = z.object({
  id: z.string().uuid().optional(),
  title: z.string().min(1, "Title is required"),
  description: z.string().optional(),
  thumbnailUrl: z.string().url().optional(),
  itemType: z.enum(["agent", "project", "template"]),
  contentId: z.string().uuid(),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
  createdBy: z.string().uuid().optional(),
  isPublic: z.boolean().default(false),
  sharedWith: z.array(z.string().uuid()).default([]),
  featured: z.boolean().default(false),
  views: z.number().default(0),
  likes: z.number().default(0),
  tags: z.array(z.string()).default([]),
  metadata: showcaseItemMetadataSchema.default({}),
});

export const showcaseInteractionSchema = z.object({
  id: z.string().uuid().optional(),
  userId: z.string().uuid(),
  itemId: z.string().uuid(),
  interactionType: z.enum(["view", "like", "share", "fork", "comment"]),
  timestamp: z.date().default(() => new Date()),
  metadata: z.record(z.string(), z.any()).optional(),
});

export const showcaseCommentSchema = z.object({
  id: z.string().uuid().optional(),
  itemId: z.string().uuid(),
  userId: z.string().uuid(),
  content: z.string().min(1, "Comment content is required"),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
  parentId: z.string().uuid().nullable().optional(),
  isEdited: z.boolean().default(false),
  isDeleted: z.boolean().default(false),
});

export const galleryFiltersSchema = z.object({
  search: z.string().optional(),
  tags: z.array(z.string()).optional(),
  capabilities: z.array(z.string()).optional(),
  complexity: z.array(z.enum(["beginner", "intermediate", "advanced"])).optional(),
  dateRange: z.object({
    start: z.date(),
    end: z.date(),
  }).optional(),
  createdBy: z.array(z.string().uuid()).optional(),
  sortBy: z.enum(["newest", "oldest", "popular", "trending", "rating"]).default("newest"),
  itemType: z.array(z.enum(["agent", "project", "template"])).optional(),
  featured: z.boolean().optional(),
  limit: z.number().min(1).max(100).default(24),
  offset: z.number().min(0).default(0),
});

// TypeScript types derived from Zod schemas
export type ShowcaseItemMetadata = z.infer<typeof showcaseItemMetadataSchema>;
export type ShowcaseItem = z.infer<typeof showcaseItemSchema>;
export type UserInteraction = z.infer<typeof showcaseInteractionSchema>;
export type ShowcaseComment = z.infer<typeof showcaseCommentSchema>;
export type GalleryFilters = z.infer<typeof galleryFiltersSchema>;

// Data mapping utilities for database interaction
export const mapDbToShowcaseItem = (dbItem: any): ShowcaseItem => {
  return {
    id: dbItem.id,
    title: dbItem.title,
    description: dbItem.description || "",
    thumbnailUrl: dbItem.thumbnail_url,
    itemType: dbItem.item_type,
    contentId: dbItem.content_id,
    createdAt: new Date(dbItem.created_at),
    updatedAt: new Date(dbItem.updated_at),
    createdBy: dbItem.created_by,
    isPublic: dbItem.is_public,
    sharedWith: dbItem.shared_with || [],
    featured: dbItem.featured,
    views: dbItem.views,
    likes: dbItem.likes,
    tags: dbItem.tags || [],
    metadata: dbItem.metadata || {},
  };
};

export const mapShowcaseItemToDb = (item: ShowcaseItem) => {
  return {
    title: item.title,
    description: item.description,
    thumbnail_url: item.thumbnailUrl,
    item_type: item.itemType,
    content_id: item.contentId,
    created_by: item.createdBy,
    is_public: item.isPublic,
    shared_with: item.sharedWith,
    featured: item.featured,
    tags: item.tags,
    metadata: item.metadata,
  };
};

export const mapDbToUserInteraction = (dbItem: any): UserInteraction => {
  return {
    id: dbItem.id,
    userId: dbItem.user_id,
    itemId: dbItem.item_id,
    interactionType: dbItem.interaction_type,
    timestamp: new Date(dbItem.created_at),
    metadata: dbItem.metadata || {},
  };
};

export const mapUserInteractionToDb = (interaction: UserInteraction) => {
  return {
    user_id: interaction.userId,
    item_id: interaction.itemId,
    interaction_type: interaction.interactionType,
    metadata: interaction.metadata || {},
  };
};

export const mapDbToShowcaseComment = (dbItem: any): ShowcaseComment => {
  return {
    id: dbItem.id,
    itemId: dbItem.item_id,
    userId: dbItem.user_id,
    content: dbItem.content,
    createdAt: new Date(dbItem.created_at),
    updatedAt: new Date(dbItem.updated_at),
    parentId: dbItem.parent_id,
    isEdited: dbItem.is_edited,
    isDeleted: dbItem.is_deleted,
  };
};

export const mapShowcaseCommentToDb = (comment: ShowcaseComment) => {
  return {
    item_id: comment.itemId,
    user_id: comment.userId,
    content: comment.content,
    parent_id: comment.parentId,
    is_edited: comment.isEdited,
    is_deleted: comment.isDeleted,
  };
};